import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Calendar as CalendarIcon } from "lucide-react";
import { getAllPosts } from "@/blog/posts";
import { useSeo, SITE, localBusinessSchema } from "@/hooks/useSeo";

function formatDate(d) {
  try {
    return new Date(d + "T12:00:00").toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return d;
  }
}

export default function Blog() {
  const posts = getAllPosts();

  useSeo({
    title: "Blog — Glenwood Gutter Guy | Gutter & Home Maintenance Tips for SW Iowa",
    description:
      "Practical gutter, screen, and home maintenance tips for Iowa homeowners — written by Mike Harrison of Glenwood Gutter Guy.",
    url: `${SITE.url}/blog`,
    type: "website",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "Glenwood Gutter Guy Blog",
      url: `${SITE.url}/blog`,
      description:
        "Practical gutter and home maintenance tips for Iowa homeowners.",
      publisher: localBusinessSchema(),
      blogPost: posts.map((p) => ({
        "@type": "BlogPosting",
        headline: p.title,
        url: `${SITE.url}/blog/${p.slug}`,
        datePublished: p.date,
        image: p.cover_image,
      })),
    },
  });

  return (
    <div className="font-body text-[#000000] bg-white min-h-screen">
      {/* Top accent */}
      <div className="h-2 bg-[#5FBE1F]" />

      {/* Simple header */}
      <header className="border-b border-[#E5E5E5]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="blog-home-link">
            <img
              src="/logo-v2.png"
              alt="Glenwood Gutter Guy"
              className="h-14 w-auto object-contain"
            />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#0A0A0A] hover:text-[#5FBE1F]"
            data-testid="blog-back-home"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to site
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mb-12"
        >
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-[#5FBE1F] mb-4">
            <span className="w-6 h-px bg-[#5FBE1F]" />
            From the truck
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#000000] leading-tight">
            Gutter &amp; home <span className="text-[#5FBE1F]">maintenance tips</span>
          </h1>
          <p className="mt-4 text-[#4A4A46] text-base sm:text-lg">
            Straight-talk articles from Mike Harrison about caring for gutters,
            screens, and the small stuff that keeps Iowa homes weather-ready.
          </p>
        </motion.div>

        {posts.length === 0 && (
          <div className="text-[#4A4A46] text-sm">
            No posts yet — check back soon.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="blog-list">
          {posts.map((post, i) => (
            <motion.article
              key={post.slug}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="group bg-white border border-[#E5E5E5] rounded-sm overflow-hidden flex flex-col hover:border-[#5FBE1F] hover:-translate-y-0.5 transition-all"
              data-testid={`blog-card-${post.slug}`}
            >
              <Link to={`/blog/${post.slug}`} className="block">
                <div
                  className="aspect-[2/1] bg-[#F5F5F5] bg-cover bg-center"
                  style={{ backgroundImage: `url('${post.cover_image}')` }}
                  role="img"
                  aria-label={post.cover_alt || post.title}
                />
              </Link>
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center gap-2 text-xs text-[#4A4A46] mb-3">
                  <CalendarIcon className="w-3.5 h-3.5" />
                  <time dateTime={post.date}>{formatDate(post.date)}</time>
                  {post.tags && post.tags.length > 0 && (
                    <>
                      <span className="text-[#9CA09B]">·</span>
                      <span className="uppercase tracking-[0.18em] text-[#5FBE1F] font-bold text-[10px]">
                        {post.tags[0]}
                      </span>
                    </>
                  )}
                </div>
                <h2 className="font-display text-xl sm:text-2xl text-[#000000] leading-snug group-hover:text-[#5FBE1F] transition-colors">
                  <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                </h2>
                <p className="mt-3 text-sm text-[#4A4A46] leading-relaxed flex-1">
                  {post.excerpt}
                </p>
                <Link
                  to={`/blog/${post.slug}`}
                  className="mt-5 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.18em] text-[#0A0A0A] hover:text-[#5FBE1F]"
                  data-testid={`blog-read-${post.slug}`}
                >
                  Read more <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>
      </main>

      <footer className="bg-[#0A0A0A] text-[#C9C9C9] py-8 mt-12">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center text-xs">
          <div>© {new Date().getFullYear()} Glenwood Gutter Guy · Mike Harrison</div>
          <Link to="/" className="hover:text-white">glenwoodgutterguy.com</Link>
        </div>
      </footer>
    </div>
  );
}
